import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { useUserLocale } from "../../customHooks/useUserLocale";
import { useActiveTripForUser, getUserWrittenReviews } from "../../services/trip";
import { currencyToCreditsGiftMap } from "../../utils/multi-language";
import { formatCurrency } from "../../utils/currency";
import { ImageResizable } from "../elements/ImageResizeable";

const SESSION_KEY = "activeTripBannerDismissed";

const ActiveTripBanner = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { currency } = useUserLocale();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(SESSION_KEY) === "true");
  const [reviewDone, setReviewDone] = useState(false);

  const isInApp = window.isInApp;
  const userId = localStorage.getItem("USER_ID");

  const onAllTrips = location.pathname === "/all-trips";
  const { data } = useActiveTripForUser(isInApp && onAllTrips && !dismissed ? userId : null);
  const activeTrip = data?.activeTrip;

  const todayStr = new Date().toISOString().split("T")[0];
  const endDateStr = activeTrip?.end_date ? activeTrip.end_date.split("T")[0] : null;
  const tripEnded = endDateStr ? todayStr >= endDateStr : false;

  useEffect(() => {
    if (!activeTrip || !tripEnded) return;
    getUserWrittenReviews(activeTrip.id)
      .then((res) => {
        if (res.data?.tripReview?.id) {
          setReviewDone(true);
        }
      })
      .catch(() => {});
  }, [activeTrip?.id, tripEnded]);

  const handleDismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem(SESSION_KEY, "true");
    setDismissed(true);
  };

  const handleActionClick = () => {
    if (!activeTrip) return;
    if (tripEnded) {
      window.postMessage(JSON.stringify({ type: "navigateToTripReview", tripId: activeTrip.id }));
    } else {
      window.postMessage(JSON.stringify({ type: "navigateToTripDetails", tripId: activeTrip.id }));
    }
  };

  if (!isInApp) return null;
  if (location.pathname !== "/all-trips") return null;
  if (dismissed) return null;
  if (!activeTrip) return null;
  if (tripEnded && reviewDone) return null;

  const surveyAmount = currencyToCreditsGiftMap[currency]?.survey || 0;
  // Club events don't pay the review credit, so the copy can't promise it
  const isClubTrip = activeTrip.broad_category === "club";
  const actionText = tripEnded
    ? isClubTrip
      ? t("active_trip_banner_review_club", {
          defaultValue: "Share your experience with us!",
        })
      : t("active_trip_banner_review", {
          amount: formatCurrency(surveyAmount, currency),
          defaultValue: "Get {{amount}} when you leave a review",
        })
    : t("active_trip_banner_view_booking", {
        defaultValue: "View booking details",
      });

  const imageUrl = activeTrip.main_images?.[0];

  return (
    <div
      className="app-floating-card tw-fixed tw-bottom-4 tw-left-6 tw-right-6 tw-z-[99998] tw-cursor-pointer tw-rounded-2xl tw-bg-white tw-p-4 tw-shadow-xl"
      // Above the app's floating tab bar (and the promo banner) when present.
      style={{ bottom: "calc(1rem + var(--app-tabbar-inset, 0px) + var(--app-bottom-banner, 0px))" }}
      onClick={handleActionClick}
    >
      <button
        className="tw-absolute tw-right-4 tw-top-0 tw-rounded-full tw-p-1 tw-text-gray-400 hover:tw-bg-gray-100"
        onClick={handleDismiss}
        aria-label="Close"
      >
        <CloseIcon style={{ fontSize: 14 }} />
      </button>
      <div className="tw-flex tw-items-center tw-gap-4">
        <div className="tw-flex-1 tw-pr-2">
          <p className="tw-text-sm tw-font-semibold tw-leading-snug tw-text-gray-900">
            {activeTrip.name}
          </p>
          <p className="tw-mt-1 tw-text-xs tw-text-gray-400">{actionText} &rsaquo;</p>
        </div>
        {imageUrl && (
          <ImageResizable
            size="s"
            src={imageUrl}
            alt={activeTrip.name}
            style={{
              width: 60,
              height: 60,
              borderRadius: 12,
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ActiveTripBanner;
